// @ts-nocheck
// TODO: align with current types (Phase 4a) — bucket 2/3 + DS strictness regressions
/**
 * Add Partner to Entity Modal
 *
 * Modal for adding a partner company to an entity (order, shipment, etc.).
 */

import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@lognai/synapse-design-system-v2';
import { Button } from '@lognai/synapse-design-system-v2';
import { Label } from '@lognai/synapse-design-system-v2';
import { Input } from '@lognai/synapse-design-system-v2';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@lognai/synapse-design-system-v2';
import { Badge } from '@lognai/synapse-design-system-v2';
import { useToast } from '@lognai/synapse-design-system-v2';
import { MultiSelect } from '../shared/MultiSelect';
import { SearchableSelect } from '../shared/SearchableSelect';
import { useAllPartnerships, useAccessObjects, useCreateEntityParticipation } from '../../hooks';
import { buildPartnerOptions } from './partner-options';
import { PartnerAccessLevel } from '../../types/domain-enums';
import type { EntityParticipationCreateInput } from '../../api';
import { entityAccessCategory, entityRoleSuggestions, entityTypeTitle } from '../../lib/entity-type';

interface AddPartnerToEntityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: string;
  entityId: string;
  ownerCompanyId: string;
  onSuccess?: () => void;
}

const addPartnerSchema = z.object({
  partnerCompanyId: z.string().min(1, 'Partner company is required'),
  role: z.string().min(2, 'Role must be at least 2 characters').max(50),
  accessLevel: z.nativeEnum(PartnerAccessLevel).default(PartnerAccessLevel.READ_ONLY),
  permissions: z.array(z.string()).default([]),
});

type AddPartnerFormData = z.infer<typeof addPartnerSchema>;

const ACCESS_LEVELS: { value: PartnerAccessLevel; label: string; description: string }[] = [
  { value: PartnerAccessLevel.READ_ONLY, label: 'Read Only', description: 'View entity details only' },
  { value: PartnerAccessLevel.READ_WRITE, label: 'Read & Write', description: 'View and update entity' },
  { value: PartnerAccessLevel.FULL, label: 'Full Access', description: 'Complete control over entity' },
];

export function AddPartnerToEntityModal({
  open,
  onOpenChange,
  entityType,
  entityId,
  ownerCompanyId,
  onSuccess,
}: AddPartnerToEntityModalProps) {
  const { toast } = useToast();

  // Every partnership of the owner company, across pages. A single page is only
  // 20 rows, so a company's later partners could never be found in this picker.
  const { data: partnershipsData } = useAllPartnerships(ownerCompanyId);
  const partnerships = partnershipsData?.items ?? [];

  /**
   * Permissions that apply to this kind of entity.
   *
   * The category used to be picked by a hand-written chain asking for
   * `ORDERS`, `SHIPMENTS` and `FINANCE` — none of which exist. The real
   * categories are singular, so those three returned zero objects and a
   * shipment offered an EMPTY permission list, while a sales order matched
   * nothing at all and fell through the chain to `INVENTORY`, offering
   * inventory permissions on an order (QA ledger ORD-004).
   *
   * An unknown entity type now filters by nothing rather than guessing: every
   * permission is a worse answer than the wrong module's, but it is at least
   * not a confident wrong one.
   */
  const accessCategory = entityAccessCategory(entityType);
  const { data: accessObjects = [] } = useAccessObjects({
    filter: accessCategory ? { category: accessCategory } : {},
  });

  const createParticipation = useCreateEntityParticipation();

  const form = useForm<AddPartnerFormData>({
    resolver: zodResolver(addPartnerSchema),
    defaultValues: {
      partnerCompanyId: '',
      role: '',
      accessLevel: PartnerAccessLevel.READ_ONLY,
      permissions: [],
    },
  });

  // See partner-options.ts for why each of these rules exists.
  const partnerOptions = buildPartnerOptions(partnerships);

  // Convert access objects to permission options
  const permissionOptions = accessObjects.map((ao) => ({
    value: ao.code,
    label: ao.name,
    description: ao.description,
  }));

  const handleSubmit = async (data: AddPartnerFormData) => {
    try {
      const participationData: EntityParticipationCreateInput = {
        entityType,
        entityId,
        companyId: data.partnerCompanyId,
        role: data.role,
        accessLevel: data.accessLevel,
        permissions: data.permissions,
      };

      await createParticipation.mutateAsync(participationData);

      toast({
        title: 'Success',
        description: 'Partner added to entity successfully',
      });

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to add partner to entity',
        variant: 'destructive',
      });
    }
  };

  const roleSuggestions = entityRoleSuggestions(entityType);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Partner to {entityTypeTitle(entityType)}</DialogTitle>
          <DialogDescription>
            Share this entity with a partner company in your network
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          {/* Partner Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Partner Company</CardTitle>
              <CardDescription>
                Select from your active partner network
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>
                  Partner <span className="text-destructive">*</span>
                </Label>
                <SearchableSelect
                  options={partnerOptions}
                  value={form.watch('partnerCompanyId')}
                  onChange={(value) =>
                    form.setValue('partnerCompanyId', value, { shouldValidate: true })
                  }
                  placeholder="Select partner company..."
                  searchPlaceholder="Search partner..."
                  emptyText="No partner found."
                  disabled={createParticipation.isPending}
                />
                {form.formState.errors.partnerCompanyId && (
                  <p className="text-sm text-destructive">{form.formState.errors.partnerCompanyId.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Role Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Role & Access</CardTitle>
              <CardDescription>
                Define the partner's role and access level
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="role">
                  Functional Role <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="role"
                  placeholder="e.g., FORWARDER, CARRIER, BUYER"
                  {...form.register('role')}
                  disabled={createParticipation.isPending}
                  className="uppercase"
                  onChange={(e) => {
                    const upper = e.target.value.toUpperCase();
                    form.setValue('role', upper, { shouldValidate: true });
                  }}
                />
                {form.formState.errors.role && (
                  <p className="text-sm text-destructive">{form.formState.errors.role.message}</p>
                )}
                <div className="flex flex-wrap gap-1 mt-2">
                  <span className="text-xs text-muted-foreground">Suggestions:</span>
                  {roleSuggestions.map((role) => (
                    <Badge
                      key={role}
                      variant="outline"
                      className="text-xs cursor-pointer hover:bg-muted"
                      onClick={() => form.setValue('role', role, { shouldValidate: true })}
                    >
                      {role}
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="accessLevel">
                  Access Level <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={form.watch('accessLevel')}
                  onValueChange={(value: PartnerAccessLevel) =>
                    form.setValue('accessLevel', value, { shouldValidate: true })
                  }
                  disabled={createParticipation.isPending}
                >
                  <SelectTrigger id="accessLevel">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCESS_LEVELS.map((level) => (
                      <SelectItem key={level.value} value={level.value}>
                        <div>
                          <div className="font-medium">{level.label}</div>
                          <div className="text-xs text-muted-foreground">{level.description}</div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Permissions */}
          <Card>
            <CardHeader>
              <CardTitle>Specific Permissions</CardTitle>
              <CardDescription>
                Optional: Grant specific access permissions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Label>Permissions</Label>
                <MultiSelect
                  options={permissionOptions}
                  value={form.watch('permissions')}
                  onChange={(values) => form.setValue('permissions', values)}
                  placeholder="Select permissions..."
                  disabled={createParticipation.isPending}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use default permissions based on access level
                </p>
              </div>
            </CardContent>
          </Card>
        </form>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createParticipation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            onClick={form.handleSubmit(handleSubmit)}
            disabled={createParticipation.isPending}
          >
            {createParticipation.isPending ? 'Adding...' : 'Add Partner'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}